<template>
  <q-dialog
    ref="dialog"
    :full-height="fullHeight"
    :full-width="fullWidth"
    :hide-scroll-indicator="hideScrollIndicator"
    :maximized="maximized"
    :no-route-dismiss="noRouteDismiss"
    :persistent="!dirtyBypass && (persistent || processing || dirty)"
    :seamless="seamless"
    :transition-hide="transitionHide"
    :transition-show="transitionShow"
    :value="visible"
    @before-hide="$emit('before-hide')"
    @before-show="$emit('before-show')"
    @hide="onHide"
    @shake="onShake"
    @show="$emit('show')"
  >
    <q-card class="column" :style="{ maxWidth: cardMaxWidth, width }">
      <q-form
        ref="form"
        class="column col no-wrap zd-form"
        :class="{ 'window-height full-width': maximized }"
        @submit="$emit('submit', $event)"
      >
        <q-card-section
          v-if="title || $scopedSlots.header"
          ref="header"
          :class="`bg-${titleColor} text-white roboto dialog--header relative ${titleDense ? 'q-py-xs q-px-md' : ''}`"
        >
          <div :class="titleDense ? 'text-subtitle1 text-weight-medium' : 'text-h6'">{{ title }}</div>
          <slot name="header" />
        </q-card-section>
        <q-card-section ref="body" class="overflow-auto dialog--body" :class="contentClass" :style="contentStyle">
          <slot />
          <q-scroll-observer @scroll="onScroll" />
          <q-resize-observer @resize="onScroll" />
        </q-card-section>
        <q-card-actions
          v-if="!hideActions"
          ref="footer"
          :align="$q.screen.xs ? 'around' : 'right'"
          class="q-py-md dialog--footer has-scroll relative content-center"
          :class="{ 'has-scroll': !hideScrollIndicator }"
        >
          <slot name="actions">
            <q-btn
              v-if="okOnly"
              v-close-popup
              class="q-px-sm"
              :class="{ 'full-width': !$q.screen.gt.sm }"
              color="dark-grey"
              :data-t="actionButtonDataT"
              :disable="processing || actionButtonDisable"
              flat
              :label="actionButtonText"
              rounded
            />
            <q-btn
              v-if="!okOnly"
              class="q-mr-xs q-px-sm"
              color="dark-grey"
              :data-t="cancelButtonDataT"
              :disable="processing || cancelButtonDisable"
              flat
              :label="cancelButtonText"
              rounded
              @click="onCancel"
            />
            <q-btn
              v-if="!okOnly"
              class="q-px-sm"
              :color="actionButtonColor"
              :data-t="actionButtonDataT"
              :disable="processing || actionButtonDisable"
              rounded
              type="submit"
            >
              <q-spinner v-if="processing" class="q-mr-sm" />
              {{ actionButtonText }}
            </q-btn>
          </slot>
        </q-card-actions>
      </q-form>
    </q-card>

    <zubie-dialog
      action-button-color="negative"
      action-button-text="Discard Changes"
      cancel-button-text="Keep Editing"
      title="Unsaved Changes"
      :visible="showDirtyConfirm"
      @hide="showDirtyConfirm = false"
      @submit="onConfirmDirtyDismiss"
      >Your changes haven't been saved. Are you sure you want to leave without saving?</zubie-dialog
    >
  </q-dialog>
</template>

<script>
import _debounce from 'lodash/debounce';
import _get from 'lodash/get';

export default {
  name: 'ZubieDialog',
  props: {
    actionButtonColor: {
      type: String,
      default: 'primary',
    },
    actionButtonDataT: String,
    actionButtonDisable: {
      type: Boolean,
      default: false,
    },
    actionButtonText: {
      type: String,
      default: 'OK',
    },
    cancelButtonDataT: String,
    cancelButtonDisable: {
      type: Boolean,
      default: false,
    },
    cancelButtonText: {
      type: String,
      default: 'Cancel',
    },
    closeOnCancel: {
      type: Boolean,
      default: true,
    },
    contentClass: String,
    contentStyle: [String, Object],
    dirty: {
      type: Boolean,
      default: false,
    },
    fullHeight: {
      type: Boolean,
      default: false,
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    hideActions: {
      type: Boolean,
      default: false,
    },
    hideScrollIndicator: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: [String, Number],
      default: '560px',
    },
    minWidth: {
      type: String,
      default: '0',
    },
    noRouteDismiss: {
      type: Boolean,
      default: false,
    },
    okOnly: {
      type: Boolean,
      default: false,
    },
    maximized: {
      type: Boolean,
      default: false,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    processing: {
      type: Boolean,
      default: false,
    },
    seamless: Boolean,
    title: String,
    titleDense: Boolean,
    titleColor: {
      type: String,
      default: 'primary',
    },
    transitionHide: {
      type: String,
      default: 'scale',
    },
    transitionShow: {
      type: String,
      default: 'scale',
    },
    visible: {
      type: Boolean,
      default: true,
    },
    width: String,
  },
  data() {
    return {
      dirtyBypass: false,
      showDirtyConfirm: false,
      scrollIndicatorWidth: '100%',
    };
  },
  computed: {
    cardMaxWidth() {
      const { maxWidth } = this;
      return Number.isFinite(maxWidth) ? `${maxWidth}px` : maxWidth;
    },
    persist() {
      const result = !this.dirtyBypass && (this.persistent || this.processing || this.dirty);
      return result;
    },
  },
  methods: {
    hide() {
      this.$refs.dialog.hide();
    },
    onCancel() {
      this.$emit('cancel');
      if (this.closeOnCancel) {
        this.hide();
      }
    },
    async onConfirmDirtyDismiss() {
      this.showDirtyConfirm = false;
      this.dirtyBypass = true;
      await this.$nextTick();
      this.hide();
    },
    onHide() {
      this.showDirtyConfirm = false;
      this.$emit('hide');
    },
    onScrollImmediate() {
      if (!this.hideScrollIndicator && _get(this.$refs, 'body.$el')) {
        const { offsetHeight, offsetWidth, scrollHeight, scrollTop, scrollWidth } = this.$refs.body.$el;
        // account for scrollbar width
        const scrollbarWidth = offsetWidth - scrollWidth;
        const scrollIndicatorWidth = `calc(100% - ${scrollbarWidth}px)`;
        if (_get(this.$refs, 'form.$el.style')) {
          this.$refs.form.$el.style.setProperty('--scroll-indicator-width', scrollIndicatorWidth);
        }
        // header
        if (scrollTop > 0 && _get(this.$refs, 'header.$el.classList')) {
          this.$refs.header.$el.classList.add('has-scroll');
        } else if (this.$refs.header) {
          this.$refs.header.$el.classList.remove('has-scroll');
        }
        // footer
        const remainingBottom = scrollHeight - offsetHeight - scrollTop;
        if (remainingBottom <= 0 && _get(this.$refs, 'footer.$el.classList')) {
          this.$refs.footer.$el.classList.remove('has-scroll');
        } else if (this.$refs.footer) {
          this.$refs.footer.$el.classList.add('has-scroll');
        }
      }
    },
    onShake() {
      // Looking for "shakes" that aren't due to processing/outside persistence
      if (!this.processing && !this.persistent && this.dirty && !this.showDirtyConfirm) {
        this.showDirtyConfirm = true;
      }
    },
    show() {
      this.$refs.dialog.show();
    },
  },
  created() {
    // use this instead of the debounce attribute on both observers so calls from both go into the same debounce queue
    this.onScroll = _debounce(this.onScrollImmediate);
  },
};
</script>

<style lang="stylus" scoped>
.zd-form {
  --scroll-indicator-width: 100%;
}

.q-card {
  overflow: hidden;
}

.q-form {
  max-height: inherit;
}

.dialog--footer {
  border-top: 1px solid $grey-5;
}

.dialog--header {
  text-transform: capitalize;
}

.dialog--header:after, .dialog--footer:before {
  content: '';
  position: absolute;
  left: 0;
  height: 50px;
  width: var(--scroll-indicator-width);
  opacity: 0;
  transition: opacity ease-in-out 0.25s;
  pointer-events: none;
}

.dialog--header:after {
  background: linear-gradient(to top, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.8) 80%), linear-gradient(to top, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0.8));
  top: 100%;
  z-index: 1;
}

.dialog--body {
  flex: 1;
}

.dialog--footer:before {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.8) 80%), linear-gradient(to bottom, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0.8));
  top: -51px; // height + 1px for the border
}

.has-scroll:before, .has-scroll:after {
  opacity: 1;
}
</style>
